(() => {
    /*
     * Prevent multiple script tags from registering the same
     * Shopify/editor listeners more than once.
     */
    if (window.AmaraTrustMarqueeLoaded) {
        window.initAmaraTrustMarquees?.(document);
        return;
    }

    window.AmaraTrustMarqueeLoaded = true;


    class AmaraTrustMarquee {
        constructor(section) {
            this.section = section;

            this.viewport =
                section.querySelector(
                    '[data-amara-marquee-viewport]'
                );

            this.track =
                section.querySelector(
                    '[data-amara-marquee-track]'
                );

            this.originalGroup =
                section.querySelector(
                    '[data-amara-marquee-group]'
                );

            if (
                !this.viewport ||
                !this.track ||
                !this.originalGroup
            ) {
                return;
            }


            /* -----------------------------------------------
               SETTINGS
               ----------------------------------------------- */

            this.duration =
                Math.max(
                    1,
                    Number(
                        section.dataset.duration
                    ) || 38
                );

            this.direction =
                section.dataset.direction === 'right'
                    ? 1
                    : -1;

            this.slowOnHover =
                section.dataset.slowOnHover === 'true';

            this.hoverSpeedPercent =
                Math.max(
                    0,
                    Math.min(
                        100,
                        Number(
                            section.dataset.hoverSpeed
                        ) || 30
                    )
                );


            /* -----------------------------------------------
               STATE
               ----------------------------------------------- */

            this.groupWidth = 0;

            this.position = 0;

            this.baseSpeed = 0;

            this.currentSpeed = 0;

            this.targetSpeed = 0;

            this.lastTime = null;

            this.frame = null;

            this.isDestroyed = false;

            this.isDocumentVisible =
                !document.hidden;


            /* -----------------------------------------------
               ACCESSIBILITY
               ----------------------------------------------- */

            this.reduceMotion =
                window.matchMedia(
                    '(prefers-reduced-motion: reduce)'
                );


            /* -----------------------------------------------
               BIND METHODS
               ----------------------------------------------- */

            this.onFrame =
                this.onFrame.bind(this);

            this.onPointerEnter =
                this.onPointerEnter.bind(this);

            this.onPointerLeave =
                this.onPointerLeave.bind(this);

            this.onFocusIn =
                this.onFocusIn.bind(this);

            this.onFocusOut =
                this.onFocusOut.bind(this);

            this.onVisibilityChange =
                this.onVisibilityChange.bind(this);

            this.onMotionChange =
                this.onMotionChange.bind(this);

            this.rebuild =
                this.debounce(
                    this.rebuild.bind(this),
                    140
                );


            this.init();
        }


        /* =====================================================
           INITIALIZATION
           ===================================================== */

        init() {
            this.bindEvents();

            /*
             * Fonts can materially affect the width of the
             * sequence. Measure again after fonts settle.
             */
            if (document.fonts?.ready) {
                document.fonts.ready.then(() => {
                    if (!this.isDestroyed) {
                        this.rebuild();
                    }
                });
            }

            this.rebuild();

            requestAnimationFrame(() => {
                this.section.classList.add(
                    'is-ready'
                );
            });
        }


        /* =====================================================
           EVENTS
           ===================================================== */

        bindEvents() {
            this.section.addEventListener(
                'mouseenter',
                this.onPointerEnter
            );

            this.section.addEventListener(
                'mouseleave',
                this.onPointerLeave
            );

            this.section.addEventListener(
                'focusin',
                this.onFocusIn
            );

            this.section.addEventListener(
                'focusout',
                this.onFocusOut
            );

            document.addEventListener(
                'visibilitychange',
                this.onVisibilityChange
            );


            if (
                typeof this.reduceMotion
                    .addEventListener === 'function'
            ) {
                this.reduceMotion.addEventListener(
                    'change',
                    this.onMotionChange
                );
            } else {
                this.reduceMotion.addListener(
                    this.onMotionChange
                );
            }


            /*
             * Recalculate if container dimensions change.
             * This covers normal browser resizing, responsive
             * Theme Editor preview changes and layout shifts.
             */
            if ('ResizeObserver' in window) {
                this.resizeObserver =
                    new ResizeObserver(() => {
                        this.rebuild();
                    });

                this.resizeObserver.observe(
                    this.viewport
                );

                this.resizeObserver.observe(
                    this.originalGroup
                );
            } else {
                window.addEventListener(
                    'resize',
                    this.rebuild,
                    { passive: true }
                );
            }
        }


        /* =====================================================
           BUILD / REBUILD
           ===================================================== */

        rebuild() {
            if (this.isDestroyed) {
                return;
            }

            this.stop();

            this.removeClones();

            this.track.style.transform =
                'translate3d(0, 0, 0)';


            this.groupWidth =
                this.originalGroup
                    .getBoundingClientRect()
                    .width;


            if (!this.groupWidth) {
                return;
            }


            /*
             * The Theme Editor setting still represents the time
             * required for ONE complete merchant-defined sequence
             * to pass.
      
             * Example:
             * group width = 1500px
             * duration    = 38 seconds
             *
             * speed ≈ 39.47px/sec
             */
            this.baseSpeed =
                this.groupWidth /
                this.duration;

            this.currentSpeed =
                this.baseSpeed;

            this.targetSpeed =
                this.baseSpeed;


            if (this.reduceMotion.matches) {
                this.position = 0;

                this.track.style.transform =
                    'none';

                return;
            }


            this.createEnoughCopies();


            /*
             * LEFT:
             * start with original group at x = 0.
             *
             * RIGHT:
             * begin one full sequence to the left so there is
             * content available to move into the viewport.
             */
            this.position =
                this.direction === 1
                    ? -this.groupWidth
                    : 0;


            this.render();

            this.start();
        }


        /* =====================================================
           DYNAMIC CLONING
           ===================================================== */

        createEnoughCopies() {
            const viewportWidth =
                this.viewport
                    .getBoundingClientRect()
                    .width;


            /*
             * Enough width for:
             *
             * current viewport
             * +
             * at least TWO extra complete sequences.
             *
             * This ensures there is always content available while
             * the coordinate wraps.
             */
            const requiredWidth =
                viewportWidth +
                this.groupWidth * 2;


            let renderedWidth =
                this.groupWidth;


            while (
                renderedWidth <
                requiredWidth
            ) {
                const clone =
                    this.originalGroup
                        .cloneNode(true);


                clone.removeAttribute(
                    'data-amara-marquee-group'
                );

                clone.dataset.amaraMarqueeClone =
                    'true';

                clone.setAttribute(
                    'aria-hidden',
                    'true'
                );


                /*
                 * Clones are visual repetitions only.
                 * They must not duplicate keyboard navigation.
                 */
                clone
                    .querySelectorAll(
                        'a, button, input, select, textarea'
                    )
                    .forEach((element) => {
                        element.setAttribute(
                            'tabindex',
                            '-1'
                        );
                    });


                /*
                 * Shopify Theme Editor metadata belongs only to
                 * the original merchant-created blocks.
                 */
                clone
                    .querySelectorAll(
                        '[data-shopify-editor-block]'
                    )
                    .forEach((element) => {
                        element.removeAttribute(
                            'data-shopify-editor-block'
                        );
                    });


                this.track.appendChild(
                    clone
                );


                renderedWidth +=
                    this.groupWidth;
            }
        }


        removeClones() {
            this.track
                .querySelectorAll(
                    '[data-amara-marquee-clone]'
                )
                .forEach((clone) => {
                    clone.remove();
                });
        }


        /* =====================================================
           ANIMATION LOOP
           ===================================================== */

        start() {
            if (
                this.frame ||
                this.reduceMotion.matches ||
                !this.isDocumentVisible
            ) {
                return;
            }


            this.lastTime =
                performance.now();


            this.frame =
                requestAnimationFrame(
                    this.onFrame
                );
        }


        stop() {
            if (!this.frame) {
                return;
            }


            cancelAnimationFrame(
                this.frame
            );


            this.frame = null;

            this.lastTime = null;
        }


        onFrame(time) {
            if (
                this.isDestroyed ||
                this.reduceMotion.matches ||
                !this.isDocumentVisible
            ) {
                this.stop();
                return;
            }


            const deltaTime =
                Math.min(
                    (
                        time -
                        this.lastTime
                    ) / 1000,
                    0.05
                );


            this.lastTime = time;


            /*
             * Smoothly interpolate between normal and hover
             * velocities.
      
             * This avoids the cheap:
             *
             * moving → STOP
             *
             * behavior of animation-play-state.
             */
            const smoothing =
                1 -
                Math.pow(
                    0.02,
                    deltaTime
                );


            this.currentSpeed +=
                (
                    this.targetSpeed -
                    this.currentSpeed
                ) *
                smoothing;


            this.position +=
                this.currentSpeed *
                deltaTime *
                this.direction;


            this.normalizePosition();

            this.render();


            this.frame =
                requestAnimationFrame(
                    this.onFrame
                );
        }


        /* =====================================================
           SEAMLESS WRAPPING
           ===================================================== */

        normalizePosition() {
            if (!this.groupWidth) {
                return;
            }


            /*
             * LEFT MOVEMENT
             *
             * Once sequence #1 has moved completely offscreen:
             *
             * [1 2 3 4 5] [1 2 3 4 5]
             *               ↑
             *
             * shift the coordinate forward by exactly one
             * sequence width.
             *
             * Because sequence #2 is identical, nothing visible
             * changes.
             */
            if (
                this.direction === -1
            ) {
                while (
                    this.position <=
                    -this.groupWidth
                ) {
                    this.position +=
                        this.groupWidth;
                }
            }


            /*
             * RIGHT MOVEMENT
             *
             * Mirror of the logic above.
             */
            if (
                this.direction === 1
            ) {
                while (
                    this.position >= 0
                ) {
                    this.position -=
                        this.groupWidth;
                }
            }
        }


        render() {
            this.track.style.transform =
                `translate3d(${this.position}px, 0, 0)`;
        }


        /* =====================================================
           PREMIUM HOVER / FOCUS SLOWDOWN
           ===================================================== */

        slowDown() {
            if (
                !this.slowOnHover ||
                this.reduceMotion.matches
            ) {
                return;
            }


            this.targetSpeed =
                this.baseSpeed *
                (
                    this.hoverSpeedPercent /
                    100
                );
        }


        speedUp() {
            this.targetSpeed =
                this.baseSpeed;
        }


        onPointerEnter() {
            if (
                window.matchMedia(
                    '(hover: hover) and (pointer: fine)'
                ).matches
            ) {
                this.slowDown();
            }
        }


        onPointerLeave() {
            if (
                !this.section.matches(
                    ':focus-within'
                )
            ) {
                this.speedUp();
            }
        }


        onFocusIn() {
            this.slowDown();
        }


        onFocusOut() {
            requestAnimationFrame(() => {
                if (
                    !this.section.matches(
                        ':focus-within'
                    )
                ) {
                    this.speedUp();
                }
            });
        }


        /* =====================================================
           VISIBILITY / ACCESSIBILITY
           ===================================================== */

        onVisibilityChange() {
            this.isDocumentVisible =
                !document.hidden;


            if (
                this.isDocumentVisible
            ) {
                this.start();
            } else {
                this.stop();
            }
        }


        onMotionChange() {
            this.rebuild();
        }


        /* =====================================================
           CLEANUP
           ===================================================== */

        destroy() {
            this.isDestroyed = true;


            this.stop();

            this.removeClones();


            this.resizeObserver?.disconnect();


            this.section.removeEventListener(
                'mouseenter',
                this.onPointerEnter
            );

            this.section.removeEventListener(
                'mouseleave',
                this.onPointerLeave
            );

            this.section.removeEventListener(
                'focusin',
                this.onFocusIn
            );

            this.section.removeEventListener(
                'focusout',
                this.onFocusOut
            );

            document.removeEventListener(
                'visibilitychange',
                this.onVisibilityChange
            );


            if (
                typeof this.reduceMotion
                    .removeEventListener === 'function'
            ) {
                this.reduceMotion
                    .removeEventListener(
                        'change',
                        this.onMotionChange
                    );
            } else {
                this.reduceMotion
                    .removeListener(
                        this.onMotionChange
                    );
            }


            window.removeEventListener(
                'resize',
                this.rebuild
            );
        }


        /* =====================================================
           UTILITY
           ===================================================== */

        debounce(callback, delay) {
            let timeout;


            return (...args) => {
                clearTimeout(timeout);


                timeout =
                    setTimeout(
                        () => {
                            callback(...args);
                        },
                        delay
                    );
            };
        }
    }


    /* =========================================================
       BOOTSTRAP
       ========================================================= */

    const instances =
        new WeakMap();


    const initSection = (section) => {
        if (
            !section ||
            instances.has(section)
        ) {
            return;
        }


        const instance =
            new AmaraTrustMarquee(
                section
            );


        instances.set(
            section,
            instance
        );
    };


    const initAll = (
        root = document
    ) => {
        /*
         * root itself may be the section when Shopify dynamically
         * reloads it, so check both root + descendants.
         */

        if (
            root.matches?.(
                '[data-amara-trust-marquee]'
            )
        ) {
            initSection(root);
        }


        root
            .querySelectorAll?.(
                '[data-amara-trust-marquee]'
            )
            .forEach(initSection);
    };


    window.initAmaraTrustMarquees =
        initAll;


    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            () => initAll()
        );
    } else {
        initAll();
    }


    /* =========================================================
       SHOPIFY THEME EDITOR SUPPORT
       ========================================================= */

    document.addEventListener(
        'shopify:section:load',
        (event) => {
            initAll(event.target);
        }
    );


    document.addEventListener(
        'shopify:section:unload',
        (event) => {
            let section = null;


            if (
                event.target.matches?.(
                    '[data-amara-trust-marquee]'
                )
            ) {
                section = event.target;
            } else {
                section =
                    event.target.querySelector?.(
                        '[data-amara-trust-marquee]'
                    );
            }


            if (!section) {
                return;
            }


            const instance =
                instances.get(section);


            if (instance) {
                instance.destroy();

                instances.delete(section);
            }
        }
    );
})();